import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './BlogPost.css';
import { db } from '../firebase';
import { doc, getDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const postRef = doc(db, "updates", id);
        const postSnap = await getDoc(postRef);
        
        if (postSnap.exists()) {
          const postData = {
            id: postSnap.id,
            ...postSnap.data()
          };
          setPost(postData);
          
          // Fetch related posts (most recent posts excluding current one)
          const relatedPostsQuery = query(
            collection(db, "updates"),
            orderBy("date", "desc"),
            limit(4)
          );
          const relatedPostsSnap = await getDocs(relatedPostsQuery);
          const relatedPostsData = relatedPostsSnap.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(relatedPost => relatedPost.id !== id)
            .slice(0, 3); // Only take up to 3 related posts
          
          setRelatedPosts(relatedPostsData);
        } else {
          setError("Post not found");
        }
      } catch (err) {
        console.error("Error fetching post:", err);
        setError("Failed to load the post");
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
    // Scroll to top when post changes
    window.scrollTo(0, 0);
  }, [id]);

  // Format date for display
  const formatDate = (dateValue) => {
    if (!dateValue) return 'Date not available';
    
    try {
      let date;
      // Check if date is a Firestore Timestamp
      if (typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      } else {
        date = new Date(dateValue);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateValue);
        return 'Invalid date';
      }
      
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return date.toLocaleDateString(undefined, options);
    } catch (error) {
      console.error('Error formatting date for display:', error);
      return 'Date error';
    }
  };

  // Truncate content for preview
  const truncateContent = (content, maxLength = 80) => {
    if (!content || content.length <= maxLength) return content || '';
    return content.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="blog-post-container">
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading post...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-post-container">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <Link to="/" className="back-link">Back to Home</Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blog-post-container">
        <div className="not-found">
          <h2>Post Not Found</h2>
          <p>The post you're looking for doesn't exist or has been removed.</p>
          <Link to="/" className="back-link">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      <div className="blog-container">
        <Link to="/" className="back-button">
          <i className="fas fa-arrow-left"></i> Back to Home
        </Link>
        
        <article className="blog-post-container">
          <div className="post-header">
            <h1>{post.title}</h1>
            <div className="post-meta">
              <span className="post-author"><i className="fas fa-user"></i> {post.author}</span>
              <span className="post-date"><i className="fas fa-calendar-alt"></i> {formatDate(post.date)}</span>
            </div>
          </div>
          
          {post.imageUrl && (
            <div className="post-image">
              <img src={post.imageUrl} alt={post.title} />
            </div>
          )}
          
          <div className="post-content">
            {post.content.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
          
          {post.sourceLink && (
            <div className="post-source">
              <a 
                href={post.sourceLink} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="source-link"
              >
                <i className="fas fa-external-link-alt"></i> View Original Source
              </a>
            </div>
          )}
        </article>
      </div>
      
      {relatedPosts.length > 0 && (
        <div className="related-posts-section">
          <div className="container">
            <h2>Related Updates</h2>
            <div className="related-posts">
              {relatedPosts.map(relatedPost => (
                <Link to={`/blog/${relatedPost.id}`} className="related-post-card" key={relatedPost.id}>
                  {relatedPost.imageUrl && (
                    <div className="related-post-image">
                      <img src={relatedPost.imageUrl} alt={relatedPost.title} />
                    </div>
                  )}
                  <div className="related-post-content">
                    <h3>{relatedPost.title}</h3>
                    <p>{truncateContent(relatedPost.content)}</p>
                    <span className="related-post-date">{formatDate(relatedPost.date)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPost; 